import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

// Default Profile Image
import ProfilePic from "../../assets/images/webisteimage/profile.jpg";

export default function Listing() {
  const { watch } = useFormContext();
  const selectedArea = watch("area");
  const routeSearch = watch("routeSearch") || "";
  const selectedBusStop = watch("selectedBusStop");

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [busStops, setBusStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedStopId, setHighlightedStopId] = useState(null);

  // Load routes when area changes
  useEffect(() => {
    setRoutes([]);
    setSelectedRoute(null);
    setBusStops([]);
    setHighlightedStopId(null);
    if (!selectedArea) return;

    const loadRoutes = async () => {
      setLoading(true);
      try {
        const res = await customFetch(`routes/by-area-with-stops/${selectedArea}`);
        setRoutes(res.status && Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error loading routes:", err);
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    };
    loadRoutes();
  }, [selectedArea]);

  // When user selects a stop from autocomplete
  useEffect(() => {
    if (!selectedBusStop) {
      setHighlightedStopId(null);
      return;
    }
    const { stop, route } = selectedBusStop;
    const fullRoute = routes.find((r) => r.rid === route.rid) || route;
    setSelectedRoute(fullRoute);
    setBusStops(fullRoute.bustops || []);
    setHighlightedStopId(stop.sid);

    setTimeout(() => {
      const el = document.getElementById(`stop-row-${stop.sid}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  }, [selectedBusStop, routes]);

  // Filter routes based on search
  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      if (!routeSearch) return true;
      const searchLower = routeSearch.toLowerCase();
      return route.bustops?.some((stop) => stop.bustopname?.toLowerCase().includes(searchLower));
    });
  }, [routes, routeSearch]);

  // Auto-select when only 1 result
  useEffect(() => {
    if (selectedBusStop) return;
    setSelectedRoute(null);
    if (routeSearch && filteredRoutes.length === 1) {
      setSelectedRoute(filteredRoutes[0]);
    }
  }, [routeSearch, routes, filteredRoutes, selectedBusStop]);

  useEffect(() => {
    if (!selectedRoute) {
      setBusStops([]);
      return;
    }
    setBusStops(selectedRoute.bustops || []);
  }, [selectedRoute]);

  const handleCardClick = (route) => {
    setHighlightedStopId(null);
    setSelectedRoute(selectedRoute?.rid === route.rid ? null : route);
  };

  const openMap = (url, e) => {
    e.stopPropagation();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  // ==================== CORRECT IMAGE URL ====================
  const getDriverImageUrl = (employeeCode) => {
    if (!employeeCode) return ProfilePic;

    const baseUrl = process.env.REACT_APP_BASE_API_URL || "http://localhost:8000/api/";
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}/uploads/${String(employeeCode).trim()}.jpg`;
  };

  // Render Card Content - Clean Layout with Image on Right
  const renderCardContent = (route, isSelected = false) => {
    const driver = route.driver || {};
    const driverImageUrl = getDriverImageUrl(driver.EmployeeCode);

    return (
      <>
        {/* Map Icon */}
        <Box
          onClick={(e) => openMap(route.map, e)}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 3,
              "&:hover": { bgcolor: "#cff5d1", transform: "scale(1.08)" },
            }}
          >
            <img
              src="https://img.icons8.com/?size=100&id=DcygmpZqBEd9&format=png&color=ffffff"
              alt="View Map"
              style={{ width: 32, height: 32 }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              mt: 0.8,
              color: "#929191",
              fontWeight: 600,
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            Click here for
            <br />
            Bus Route Map
          </Typography>
        </Box>

        <CardContent sx={{ p: 3, pr: 11 }}>
          <Typography variant="h5" fontWeight="bold" color="#4caf50" gutterBottom>
            Route {route.routeno}
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 2, minHeight: 45 }}>
            {route.routename}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Area:</strong> {route.areas?.[0]?.areaname || "N/A"}
            {route.areas?.[0]?.code && ` (${route.areas[0].code})`}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Bus Number:</strong> {route.BusNumber || "N/A"}
          </Typography>

          {/* Driver Details Section */}
          <Box mt={3} pt={2} borderTop="1px dashed #e0e0e0">
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
              Driver Details
            </Typography>

            <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
              {/* Left Side - Driver Information */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                  {driver.DriverName || "Not Assigned"}
                </Typography>
                {driver.ContactNumber && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                    {driver.ContactNumber}
                  </Typography>
                )}
                {!isSelected && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { color: "#1565c0" },
                    }}
                  >
                    Click here for info
                  </Typography>
                )}
              </Box>

              {/* Right Side - Driver Image (Rectangle) */}
              <Box
                sx={{
                  width: 95,
                  height: 95,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "3px solid #f0f0f0",
                  flexShrink: 0,
                  boxShadow: 2,
                }}
              >
                <img
                  src={driverImageUrl}
                  alt={driver.DriverName || "Driver"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.src = ProfilePic; // Show default image if upload fails
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {!selectedArea && !loading && (
        <Box
          textAlign="center"
          sx={{ py: 8, px: 4, bgcolor: "background.paper", borderRadius: 3, boxShadow: 2 }}
        >
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            Welcome to Bus Route Finder
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            Please select an Emirate and Area from the dropdowns above to view available bus routes
          </Typography>
        </Box>
      )}

      {loading && (
        <Box textAlign="center" my={6}>
          <CircularProgress size={50} sx={{ color: "#4caf50" }} />
          <Typography variant="h6" mt={2} color="text.secondary">
            Loading routes...
          </Typography>
        </Box>
      )}

      {!loading && routes.length === 0 && selectedArea && (
        <Alert severity="info" sx={{ my: 4, borderRadius: 2 }}>
          No routes available for the selected area.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Route Cards */}
        <Grid item xs={12} md={selectedRoute ? 4 : 12}>
          <Grid container spacing={2}>
            {selectedRoute ? (
              <Grid item xs={12}>
                <Card
                  onClick={() => handleCardClick(selectedRoute)}
                  sx={{
                    cursor: "pointer",
                    border: "3px solid #4caf50",
                    borderRadius: 3,
                    boxShadow: 8,
                    position: "relative",
                  }}
                >
                  {renderCardContent(selectedRoute, true)}
                </Card>
              </Grid>
            ) : (
              filteredRoutes.map((route) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={route.rid}>
                  <Card
                    onClick={() => handleCardClick(route)}
                    sx={{
                      cursor: "pointer",
                      border: "2px solid transparent",
                      borderRadius: 3,
                      boxShadow: 3,
                      height: "100%",
                      position: "relative",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: 8,
                        transform: "translateY(-4px)",
                        borderColor: "#4caf50",
                      },
                    }}
                  >
                    {renderCardContent(route, false)}
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Grid>

        {/* Right: Bus Stops Table */}
        {selectedRoute && (
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                position: { md: "sticky" },
                top: { md: 80 },
                bgcolor: "background.paper",
                borderRadius: 3,
                boxShadow: 4,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                textAlign="center"
                color="#4caf50"
                fontWeight="bold"
                sx={{ mb: 3 }}
              >
                Pickup & Drop-off Points – Route {selectedRoute.routeno}
              </Typography>

              {busStops.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "60px 1fr 140px 140px 140px",
                      bgcolor: "white",
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: 2,
                      minWidth: 600,
                    }}
                  >
                    {["#", "Bus Stop", "Pickup Code", "Pickup Time", "Drop-off Time"].map(
                      (header, idx) => (
                        <Box
                          key={header}
                          sx={{
                            bgcolor: "#4caf50",
                            color: "white",
                            p: 1.5,
                            fontWeight: "bold",
                            textAlign: idx === 1 ? "left" : "center",
                            borderRight: idx < 4 ? "1px solid rgba(255,255,255,0.2)" : "none",
                          }}
                        >
                          {header}
                        </Box>
                      )
                    )}

                    {busStops.map((stop, i) => {
                      const isHighlighted = highlightedStopId === stop.sid;
                      const baseBg = isHighlighted ? "#c8e6c9" : i % 2 === 0 ? "white" : "#f5f5f5";
                      return (
                        <React.Fragment key={stop.sid}>
                          {[
                            { content: i + 1, align: "center" },
                            { content: stop.bustopname || "—", align: "left" },
                            { content: stop.pickupcode || "—", align: "center" },
                            { content: stop.pickuptime || "—", align: "center" },
                            { content: stop.dropofftime || "—", align: "center" },
                          ].map((cell, cellIdx) => (
                            <Box
                              key={cellIdx}
                              id={cellIdx === 0 ? `stop-row-${stop.sid}` : undefined}
                              sx={{
                                p: 1.5,
                                textAlign: cell.align,
                                bgcolor: baseBg,
                                borderRight: cellIdx < 4 ? "1px solid #e0e0e0" : "none",
                                borderTop: "1px solid #e0e0e0",
                                fontWeight: isHighlighted ? "bold" : "normal",
                              }}
                            >
                              {cell.content}
                            </Box>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                  No pickup/drop-off points available for this route.
                </Alert>
              )}

              <Box textAlign="center" mt={3}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Iconify icon="mdi:arrow-left-bold" />}
                  onClick={() => {
                    setSelectedRoute(null);
                    setHighlightedStopId(null);
                  }}
                  sx={{
                    bgcolor: "#4caf50",
                    "&:hover": { bgcolor: "#388e3c" },
                    borderRadius: 2,
                    px: 6,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  BACK TO ALL ROUTES
                </Button>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
